// stores/auth.ts
import { defineStore } from 'pinia';
import { external } from '../apis/external';
import { jwtDecode } from 'jwt-decode';
import {ProfileResult} from '../types/external';

export const useAuthStore = defineStore('authStorage', {
	state: () => ({
		profile: {} as ProfileResult | null,
		token: '' as string | null,
		isLoading: false,
		error: '' as string | null,
		isLoggedIn: false,
	}),

	actions: {
		async login(payload: { username: string; password: string }) {
			this.isLoading = true;
			this.error = null;

			try {
				const resp = await external.login(payload);
				if (resp.code !== '0') throw new Error(resp.message);
				if (!resp.result.token) throw new Error('ไม่พบ token');

				this.setData({ token: resp.result.token });
				await this.fetchData();
				this.updateIsLoggedIn();
			} catch (error: any) {
				this.error = error || 'เกิดข้อผิดพลาด';
				throw error;
			} finally {
				this.isLoading = false;
			}
		},

		setData({ token }: { token: string }) {
			this.token = token;
			localStorage.setItem('auth_token', token);
			this.updateIsLoggedIn();
		},

		hydrate() {
			this.token = localStorage.getItem('auth_token');
			this.updateIsLoggedIn();

			const raw = localStorage.getItem('auth_user');
			if (raw && raw !== 'undefined' && raw !== 'null') {
				try {
					this.profile = JSON.parse(raw);
				} catch {
					this.profile = null;
				}
			}
		},

		async logout() {
			const resp = await external.logout({ data: null });
			if (resp.code !== '0') throw new Error(resp.message);

			this.profile = null;
			this.token = null;
			localStorage.removeItem('auth_token');
			localStorage.removeItem('auth_user');
			this.updateIsLoggedIn();
		},

		updateIsLoggedIn() {
			try {
				const decoded: any = this.token ? jwtDecode(this.token) : null;
				this.isLoggedIn = !!decoded && !decoded.data?.isExpire;
			} catch {
				this.isLoggedIn = false;
			}
		},

		async fetchData() {
			this.isLoading = true;
			this.error = null;

			try {
				const resp = await external.profile({ data: null });
				if (resp.code === '4010') {
					await this.logout();
					throw new Error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่');
				}

				if (resp.code === '8017' || resp.code === '9902') {
					await this.logout();
					throw new Error(resp.message);
				}

				if (resp.code !== '0') throw new Error(resp.message);

				this.profile = resp.result;
				localStorage.setItem('auth_user', JSON.stringify(resp.result));
			} catch (error: any) {
				this.error = error || 'เกิดข้อผิดพลาด';
				throw error;
			} finally {
				this.isLoading = false;
			}
		},
	},
});
